import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapCrmOrganizationUnitDeleteResult,
  mapCrmOrganizationUnitItem,
  mapCrmOrganizationUnitListPage,
  mapCrmOrganizationUnitListParams,
  mapCrmOrganizationUnitMutationPayload,
  mapCrmOrganizationUnitTreeNode,
  type CrmOrganizationUnitDeleteResultRaw,
  type CrmOrganizationUnitItemRaw,
  type CrmOrganizationUnitListPageRaw,
  type CrmOrganizationUnitTreeNodeRaw,
} from "../api/adapters";
import type {
  CrmOrganizationUnitDeleteResult,
  CrmOrganizationUnitItem,
  CrmOrganizationUnitListPage,
  CrmOrganizationUnitListParams,
  CrmOrganizationUnitMutation,
  CrmOrganizationUnitTreeNode,
} from "../types";

/** 查询组织单元分页列表。 */
export async function fetchCrmOrganizationUnitList(
  params: CrmOrganizationUnitListParams,
): Promise<CrmOrganizationUnitListPage> {
  const response = await httpClient.get<ApiEnvelope<CrmOrganizationUnitListPageRaw>>(
    "/crm/organization-units",
    {
      params: mapCrmOrganizationUnitListParams(params),
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("组织单元列表接口返回了无效数据");
  }
  return mapCrmOrganizationUnitListPage(response.data.data);
}

/** 创建组织单元。 */
/** 按相同筛选条件拉取完整组织单元列表，用于按学校定位时避免分页切断树形结构。 */
export async function fetchCrmOrganizationUnitCompleteList(
  params: CrmOrganizationUnitListParams,
): Promise<CrmOrganizationUnitListPage> {
  const pageSize = 100;
  const firstPage = await fetchCrmOrganizationUnitList({
    ...params,
    page: 1,
    pageSize,
  });
  const items = [...firstPage.items];
  const totalPages = Math.ceil(firstPage.total / pageSize);

  for (let page = 2; page <= totalPages; page += 1) {
    const nextPage = await fetchCrmOrganizationUnitList({
      ...params,
      page,
      pageSize,
    });
    items.push(...nextPage.items);
  }

  return {
    items,
    page: 1,
    pageSize,
    total: firstPage.total,
  };
}

/** 创建组织单元。 */
export async function createCrmOrganizationUnit(
  payload: CrmOrganizationUnitMutation,
): Promise<CrmOrganizationUnitItem> {
  const response = await httpClient.post<ApiEnvelope<CrmOrganizationUnitItemRaw>>(
    "/crm/organization-units",
    mapCrmOrganizationUnitMutationPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("组织单元创建接口返回了无效数据");
  }
  return mapCrmOrganizationUnitItem(response.data.data);
}

/** 更新组织单元。 */
export async function updateCrmOrganizationUnit(
  id: number,
  payload: CrmOrganizationUnitMutation,
): Promise<CrmOrganizationUnitItem> {
  const response = await httpClient.put<ApiEnvelope<CrmOrganizationUnitItemRaw>>(
    `/crm/organization-units/${id}`,
    mapCrmOrganizationUnitMutationPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("组织单元更新接口返回了无效数据");
  }
  return mapCrmOrganizationUnitItem(response.data.data);
}

/** 启用或停用组织单元。 */
export async function setCrmOrganizationUnitDisabled(
  id: number,
  isDisabled: boolean,
): Promise<CrmOrganizationUnitItem> {
  const response = await httpClient.patch<ApiEnvelope<CrmOrganizationUnitItemRaw>>(
    `/crm/organization-units/${id}/disabled`,
    { is_disabled: isDisabled },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("组织单元启停接口返回了无效数据");
  }
  return mapCrmOrganizationUnitItem(response.data.data);
}

/** 删除组织单元。 */
export async function deleteCrmOrganizationUnit(
  id: number,
): Promise<CrmOrganizationUnitDeleteResult> {
  const response = await httpClient.delete<ApiEnvelope<CrmOrganizationUnitDeleteResultRaw>>(
    `/crm/organization-units/${id}`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("组织单元删除接口返回了无效数据");
  }
  return mapCrmOrganizationUnitDeleteResult(response.data.data);
}

/** 查询某学校完整组织单元树。 */
export async function fetchCrmOrganizationUnitTree(
  organizationId: number,
): Promise<CrmOrganizationUnitTreeNode> {
  const response = await httpClient.get<ApiEnvelope<CrmOrganizationUnitTreeNodeRaw>>(
    `/crm/organizations/${organizationId}/units/tree`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("组织单元树接口返回了无效数据");
  }
  return mapCrmOrganizationUnitTreeNode(response.data.data);
}
